// Reusable open-slot picker for booking + rescheduling. Reads the doctor's live availability from
// the API (which already subtracts PTO/blocks + existing bookings and applies the lead window), so
// the picker only renders genuinely free times. A stale pick is rejected server-side (race-safe).
//
// Presents a month-calendar grid (prev/next navigation); clicking an available day reveals that
// day's open times below. Used by both the booking flow (step 3) and the reschedule modal.
import { useMemo, useState } from "react";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { formatTime } from "../lib/format";
import { Loading, ErrorState, EmptyState } from "./ui";

const HORIZON_DAYS = 60;
const MS_PER_DAY = 86400000;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOW_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Local-day key (YYYY-M-D in the user's zone) so slots group by the calendar day they fall on.
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function midnight(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function SlotPicker({
  doctorId,
  value,
  onChange,
}: {
  doctorId: string;
  value: string | null;
  onChange: (iso: string) => void;
}) {
  const api = useApi();
  const from = new Date().toISOString();
  const to = new Date(Date.now() + HORIZON_DAYS * MS_PER_DAY).toISOString();
  const { data, loading, error } = useAsync(() => api.availability(doctorId, from, to), [doctorId]);

  const slots = useMemo(() => data?.slots ?? [], [data]);

  // Group slots by local calendar day → ISO list. Only days with ≥1 slot are selectable.
  const slotsByDay = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const s of slots) {
      const key = dayKey(new Date(s));
      map.set(key, [...(map.get(key) ?? []), s]);
    }
    return map;
  }, [slots]);

  const today = midnight(new Date());

  // Default the selected day to the first day that actually has availability.
  const firstAvailableDate = useMemo(() => {
    const first = slots[0];
    if (!first) return null;
    return midnight(new Date(first));
  }, [slots]);

  const [view, setView] = useState(() => {
    const base = firstAvailableDate ?? today;
    return { year: base.getFullYear(), month: base.getMonth() };
  });
  const [selectedKey, setSelectedKey] = useState<string | null>(
    () => (firstAvailableDate ? dayKey(firstAvailableDate) : null),
  );

  if (loading) return <Loading label="Finding open times…" />;
  if (error) return <ErrorState message={error} />;
  if (!slots.length) return <EmptyState>No open times in the next two months.</EmptyState>;

  const monthStart = new Date(view.year, view.month, 1);
  const firstDow = monthStart.getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const isCurrentMonth = today.getFullYear() === view.year && today.getMonth() === view.month;
  // Don't page past the horizon — last selectable day is HORIZON_DAYS out.
  const horizonEnd = midnight(new Date(Date.now() + HORIZON_DAYS * MS_PER_DAY));
  const maxMonthStart = new Date(horizonEnd.getFullYear(), horizonEnd.getMonth(), 1);
  const canPrev = !isCurrentMonth;
  const canNext = monthStart < maxMonthStart;

  const prevMonth = () => {
    let m = view.month - 1;
    let y = view.year;
    if (m < 0) { m = 11; y -= 1; }
    setView({ year: y, month: m });
  };
  const nextMonth = () => {
    let m = view.month + 1;
    let y = view.year;
    if (m > 11) { m = 0; y += 1; }
    setView({ year: y, month: m });
  };

  const cells: JSX.Element[] = [];
  for (let i = 0; i < firstDow; i++) {
    cells.push(<div key={`e${i}`} className="cal-cell empty" />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const cellDate = midnight(new Date(view.year, view.month, day));
    const offset = Math.round((cellDate.getTime() - today.getTime()) / MS_PER_DAY);
    const key = dayKey(cellDate);
    const hasSlots = slotsByDay.has(key);
    const isPast = offset < 0;
    const isAvailable = !isPast && hasSlots;

    let cls = "cal-cell";
    if (isPast) cls += " past";
    else if (isAvailable) cls += " available";
    if (offset === 0) cls += " today";
    if (selectedKey === key) cls += " selected";

    cells.push(
      <button
        key={key}
        type="button"
        className={cls}
        disabled={!isAvailable}
        aria-label={`${MONTH_NAMES[view.month]} ${day}`}
        onClick={isAvailable ? () => setSelectedKey(key) : undefined}
      >
        {day}
      </button>,
    );
  }

  const daySlots = selectedKey ? slotsByDay.get(selectedKey) ?? [] : [];
  const selectedLabel = selectedKey
    ? new Date(daySlots[0] ?? Date.now()).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="slot-picker">
      <div className="cal-widget">
        <div className="cal-head">
          <div className="cal-month">
            {MONTH_NAMES[view.month]} <em>{view.year}</em>
          </div>
          <div className="cal-nav">
            <button type="button" onClick={prevMonth} disabled={!canPrev} aria-label="Previous month">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <button type="button" onClick={nextMonth} disabled={!canNext} aria-label="Next month">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
        </div>
        <div className="cal-grid">
          {DOW_HEADERS.map((d) => (
            <div key={d} className="cal-dow">{d}</div>
          ))}
          {cells}
        </div>
      </div>

      {selectedKey === null ? (
        <div className="cal-empty">Pick a day above to see available times.</div>
      ) : daySlots.length === 0 ? (
        <>
          <div className="cal-selected-day">No slots available for <em>{selectedLabel}</em>.</div>
          <div className="cal-empty">Try another day above.</div>
        </>
      ) : (
        <>
          <div className="cal-selected-day">Available · <em>{selectedLabel}</em></div>
          <div className="slots-grid">
            {daySlots.map((s) => (
              <button
                key={s}
                type="button"
                className={`slot ${value === s ? "selected" : ""}`}
                onClick={() => onChange(s)}
              >
                {formatTime(s)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
